import { Download, ExternalLink, Loader2, RefreshCw, ShieldCheck } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { RoleSelect } from "@/components/role-select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAsync } from "@/hooks/use-async";
import { useAdminKey } from "@/hooks/use-auth";
import {
  adminApi,
  ApiError,
  type GeneratedKey,
  type SlackSyncAction,
  type SlackSyncEntry,
  type SlackSyncPreview,
  type StudentRole,
  type SyncVerdict,
} from "@/lib/api";

/**
 * The verdicts that propose a change, in the order they are shown, with how each is
 * turned into an action once ticked.
 */
const ACTIONABLE: Record<
  "new" | "left" | "email_changed" | "link",
  { label: string; blurb: string; tone: "default" | "secondary" | "destructive"; button: string }
> = {
  new: {
    label: "In Slack, not enrolled",
    blurb: "Active accounts with no enrollment here. Each ticked one is enrolled and issued a key. Workspace admins are proposed as staff.",
    tone: "default",
    button: "Enrol",
  },
  left: {
    label: "Deactivated in Slack, still holding a key",
    blurb: "The workspace says they have left. Ticked ones have every key disabled; their submissions stay attributable.",
    tone: "destructive",
    button: "Disable keys of",
  },
  email_changed: {
    label: "Profile address changed",
    blurb: "Same Slack account, different email on the profile. Ticked ones have their enrollment moved to the new address.",
    tone: "secondary",
    button: "Update",
  },
  link: {
    label: "Enrolled without a Slack id",
    blurb: "Rows from a CSV whose address matches an active account. Linking records the id so later syncs and messages find them even if the address changes.",
    tone: "secondary",
    button: "Link",
  },
};

const INFORMATIONAL: Record<"missing" | "unmappable" | "staff" | "ok", { label: string; blurb: string }> = {
  missing: { label: "Enrolled, no Slack account", blurb: "Nothing matches by id or address. They may not have joined the workspace yet." },
  unmappable: { label: "In Slack, no email", blurb: "Usually guests. Nothing to enrol on." },
  staff: { label: "Staff", blurb: "The teaching team. Listed, never acted on." },
  ok: { label: "In agreement", blurb: "" },
};

const ORDER: SyncVerdict[] = ["new", "left", "email_changed", "link", "missing", "unmappable", "staff", "ok"];

type Key = string; // `${verdict}:${slack_user_id ?? student_id}`
const keyOf = (e: SlackSyncEntry): Key => `${e.verdict}:${e.slack_user_id ?? `s${e.student_id}`}`;

/**
 * Reconcile the course with the Slack workspace.
 *
 * Read-only until "Apply": the preview groups every disagreement by what it proposes,
 * everything proposed is pre-ticked, and the operator unticks exceptions. Newcomers can
 * have their role flipped before enrolment. Applying re-checks each row server-side, and
 * the table is re-read afterwards so it shows what now exists rather than what was true
 * before the write.
 *
 * The other direction -- keys disabled here while the account is still on in Slack --
 * is a list at the bottom, because Slack offers no API for deactivating accounts on
 * this plan and somebody has to go and click.
 */
export function SlackSync({ courseId, workspaceUrl }: { courseId: string; workspaceUrl?: string }) {
  const adminKey = useAdminKey();
  // Read on arrival: it writes nothing, and a page whose only content is a button to
  // fetch the page is a click for no reason.
  const state = useAsync(adminKey ? () => adminApi.slackSync(courseId, adminKey) : null, [adminKey, courseId]);
  const preview: SlackSyncPreview | null = state.data;
  const [busy, setBusy] = useState(false);
  /**
   * Selection and roles are stored as *departures from the default* -- everything
   * proposed is ticked and a newcomer's role is what the server proposed -- so a fresh
   * read needs no effect to reset them; the handlers clear them when they re-read.
   */
  const [unticked, setUnticked] = useState<Set<Key>>(new Set());
  const [roleOverrides, setRoleOverrides] = useState<Record<string, StudentRole>>({});
  const [issued, setIssued] = useState<GeneratedKey[] | null>(null);
  const loading = state.loading || busy;

  const grouped = useMemo(() => {
    const out = new Map<SyncVerdict, SlackSyncEntry[]>();
    for (const verdict of ORDER) out.set(verdict, []);
    for (const entry of preview?.entries ?? []) out.get(entry.verdict)?.push(entry);
    return out;
  }, [preview]);

  const actionable = useMemo(() => (preview?.entries ?? []).filter((e) => e.verdict in ACTIONABLE), [preview]);
  const isTicked = (e: SlackSyncEntry) => !unticked.has(keyOf(e));
  const roleFor = (e: SlackSyncEntry): StudentRole => roleOverrides[e.slack_user_id ?? ""] ?? e.proposed_role ?? "student";

  function reload() {
    setUnticked(new Set());
    setRoleOverrides({});
    state.refetch();
  }

  function toAction(e: SlackSyncEntry): SlackSyncAction | null {
    const uid = e.slack_user_id ?? "";
    switch (e.verdict) {
      case "new":
        return { action: "enrol", slack_user_id: uid, email: e.slack_email!, name: e.slack_display_name, role: roleFor(e) };
      case "left":
        return { action: "deactivate", slack_user_id: uid || `s${e.student_id}`, student_id: e.student_id! };
      case "link":
        return { action: "link", slack_user_id: uid, student_id: e.student_id! };
      case "email_changed":
        return { action: "update_email", slack_user_id: uid, student_id: e.student_id!, email: e.slack_email! };
      default:
        return null;
    }
  }

  async function apply() {
    if (!adminKey) return;
    const actions = actionable.filter(isTicked).map(toAction).filter((a): a is SlackSyncAction => a !== null);
    if (!actions.length) return;
    setBusy(true);
    try {
      const result = await adminApi.slackSyncApply(courseId, actions, adminKey);
      setIssued(result.created);
      const parts = [
        result.created.length && `enrolled ${result.created.length}`,
        result.deactivated.length && `disabled ${result.deactivated.length}`,
        result.updated.length && `updated ${result.updated.length}`,
        result.linked.length && `linked ${result.linked.length}`,
        result.skipped.length && `skipped ${result.skipped.length}`,
      ].filter(Boolean);
      toast.success(parts.join(", ") || "Nothing to do");
      if (result.skipped.length) {
        toast.message("Skipped", { description: result.skipped.map((s) => s.reason).join("\n"), duration: 15000 });
      }
      reload();
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "Failed", { duration: 12000 });
    } finally {
      setBusy(false);
    }
  }

  const toggle = (k: Key) =>
    setUnticked((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });

  const ticked = actionable.filter(isTicked);
  const tickedBy = (v: SyncVerdict) => ticked.filter((e) => e.verdict === v).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" size="sm" onClick={reload} disabled={loading}>
          <RefreshCw className={loading ? "size-4 animate-spin" : "size-4"} aria-hidden />
          Re-read workspace
        </Button>
        <p className="text-muted-foreground text-xs">
          Every account in the workspace, compared with this course's enrollment. Nothing is written until you apply.
        </p>
      </div>

      {issued && issued.length ? <IssuedKeys created={issued} /> : null}

      {state.error ? (
        <p className="text-destructive rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">{state.error.message}</p>
      ) : null}
      {state.loading && !preview ? (
        <p className="text-muted-foreground flex items-center gap-2 text-sm">
          <Loader2 className="size-4 animate-spin" aria-hidden />
          Reading the workspace…
        </p>
      ) : null}

      {preview ? (
        <>
          <Summary counts={preview.counts} todo={preview.slack_todo.length} />

          {ORDER.map((verdict) => {
            const entries = grouped.get(verdict) ?? [];
            if (!entries.length) return null;
            const isAction = verdict in ACTIONABLE;
            const meta = isAction ? ACTIONABLE[verdict as keyof typeof ACTIONABLE] : INFORMATIONAL[verdict as keyof typeof INFORMATIONAL];
            if (verdict === "ok") {
              return (
                <p key={verdict} className="text-muted-foreground flex items-center gap-2 text-xs">
                  <ShieldCheck className="size-3.5" aria-hidden />
                  {entries.length} in agreement.
                </p>
              );
            }
            return (
              <Card key={verdict}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <Badge variant={isAction ? (meta as (typeof ACTIONABLE)["new"]).tone : "secondary"}>{entries.length}</Badge>
                    {meta.label}
                  </CardTitle>
                  {meta.blurb ? <CardDescription className="text-xs">{meta.blurb}</CardDescription> : null}
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {isAction ? (
                          <TableHead className="w-10">
                            <span className="sr-only">Include</span>
                          </TableHead>
                        ) : null}
                        <TableHead>Slack</TableHead>
                        <TableHead>Enrollment</TableHead>
                        {verdict === "new" ? <TableHead className="w-28">Role</TableHead> : null}
                        <TableHead>Notes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {entries.map((entry) => {
                        const k = keyOf(entry);
                        return (
                          <TableRow key={k}>
                            {isAction ? (
                              <TableCell>
                                <Checkbox aria-label={`Include ${entry.email ?? entry.slack_email}`} checked={isTicked(entry)} onCheckedChange={() => toggle(k)} />
                              </TableCell>
                            ) : null}
                            <TableCell className="text-xs">
                              {entry.slack_user_id ? (
                                <>
                                  <div className="flex items-center gap-1.5 text-sm">
                                    {entry.slack_display_name}
                                    {entry.slack_is_admin ? <Badge variant="outline" className="text-[10px]">admin</Badge> : null}
                                    {entry.slack_deactivated ? <Badge variant="destructive" className="text-[10px]">deactivated</Badge> : null}
                                  </div>
                                  <div className="text-muted-foreground font-mono">{entry.slack_email ?? "no email"}</div>
                                </>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-xs">
                              {entry.student_id ? (
                                <>
                                  <div className="flex items-center gap-1.5 text-sm">
                                    {entry.name || <span className="text-muted-foreground">unnamed</span>}
                                    {entry.role === "staff" ? <Badge variant="outline" className="text-[10px]">staff</Badge> : null}
                                    {!entry.has_active_key ? <Badge variant="secondary" className="text-[10px]">no active key</Badge> : null}
                                  </div>
                                  <div className="text-muted-foreground font-mono">{entry.email}</div>
                                </>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            {verdict === "new" ? (
                              <TableCell>
                                <RoleSelect
                                  value={roleFor(entry)}
                                  onChange={(role) => setRoleOverrides((prev) => ({ ...prev, [entry.slack_user_id!]: role }))}
                                />
                              </TableCell>
                            ) : null}
                            <TableCell className="text-muted-foreground space-y-1 text-xs">
                              {entry.reason ? <p>{entry.reason}</p> : null}
                              {entry.also_enrolled_in.length ? <p>Also enrolled in {entry.also_enrolled_in.join(", ")}</p> : null}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            );
          })}

          {actionable.length ? (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
              <p className="text-muted-foreground text-xs">
                {ticked.length} of {actionable.length} selected
                {ticked.length
                  ? ` — ${(Object.keys(ACTIONABLE) as (keyof typeof ACTIONABLE)[])
                      .filter((v) => tickedBy(v))
                      .map((v) => `${ACTIONABLE[v].button.toLowerCase()} ${tickedBy(v)}`)
                      .join(", ")}`
                  : ""}
                .
              </p>
              <Button onClick={apply} disabled={!ticked.length || loading}>
                Apply {ticked.length} change{ticked.length === 1 ? "" : "s"}
              </Button>
            </div>
          ) : (
            <p className="text-muted-foreground border-t pt-4 text-xs">Nothing to change. The course and the workspace agree.</p>
          )}

          {preview.slack_todo.length ? <SlackTodo entries={preview.slack_todo} workspaceUrl={workspaceUrl} /> : null}
        </>
      ) : null}
    </div>
  );
}

/** One line of numbers, so the shape of the situation is visible before the cards. */
function Summary({ counts, todo }: { counts: Record<SyncVerdict, number>; todo: number }) {
  const items: { label: string; n: number; tone?: "destructive" | "default" }[] = [
    { label: "in agreement", n: counts.ok },
    { label: "to enrol", n: counts.new, tone: "default" },
    { label: "left", n: counts.left, tone: "destructive" },
    { label: "address changed", n: counts.email_changed },
    { label: "to link", n: counts.link },
    { label: "no Slack account", n: counts.missing },
    { label: "staff", n: counts.staff },
    { label: "to disable in Slack", n: todo },
  ];
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
      {items
        .filter((i) => i.n > 0)
        .map((i) => (
          <span key={i.label} className="flex items-center gap-1.5">
            <Badge variant={i.tone ?? "secondary"} className="px-1.5 font-mono">
              {i.n}
            </Badge>
            <span className="text-muted-foreground">{i.label}</span>
          </span>
        ))}
    </div>
  );
}

/**
 * The direction this tool cannot take. Slack's API deactivates accounts only on
 * Business+/Enterprise plans, so these rows are for a person with the admin page open.
 */
function SlackTodo({ entries, workspaceUrl }: { entries: SlackSyncEntry[]; workspaceUrl?: string }) {
  const adminUrl = workspaceUrl ? `${workspaceUrl.replace(/\/$/, "")}/admin` : null;
  return (
    <Card className="border-dashed">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <Badge variant="secondary">{entries.length}</Badge>
          To do in Slack: disabled here, still active there
        </CardTitle>
        <CardDescription className="text-xs">
          Every key of these students is disabled on this platform, but their Slack account is still on. Slack has no API for
          deactivating accounts on this plan, so this is a list for whoever has the admin page open.
          {adminUrl ? (
            <>
              {" "}
              <a href={adminUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline">
                Open workspace admin <ExternalLink className="size-3" aria-hidden />
              </a>
            </>
          ) : null}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="grid gap-1 text-xs sm:grid-cols-2">
          {entries.map((e) => (
            <li key={e.student_id} className="flex items-baseline gap-2">
              <span className="text-sm">{e.slack_display_name || e.name}</span>
              <span className="text-muted-foreground font-mono">{e.slack_email ?? e.email}</span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

/**
 * The keys just issued, offered as a download.
 *
 * Shown once and not fetched again, because this is the only moment they are grouped as
 * "the ones you just created". They remain visible on the Students page, but finding
 * fifteen new rows among sixty is not the same as having them listed.
 */
function IssuedKeys({ created }: { created: GeneratedKey[] }) {
  function download() {
    const csv = ["email,name,key", ...created.map((c) => `${c.email},${c.name},${c.key}`)].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "issued-keys.csv";
    anchor.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">
          Issued {created.length} key{created.length === 1 ? "" : "s"}
        </CardTitle>
        <CardDescription className="text-xs">
          Send these out with the Messages tab, which reads each student's key from their record — no copying required.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button variant="outline" size="sm" onClick={download}>
          <Download className="size-4" aria-hidden />
          Download CSV
        </Button>
      </CardContent>
    </Card>
  );
}
